import React from "react";
import { useTranslation } from "react-i18next";
import { useGetCartQuery } from "../../api/cartApi";
import CartPageSkeleton from "./CartPageSkeleton";
import CartPageList from "./CartPageList";
import CartApplyCouponForm from "../common/CartApplyCouponForm";
import CartShippingCalculator from "../common/CartShippingCalculator";
import FreeShippingProgressBar from "../common/FreeShippingProgressBar";
import FirstPurchaseBanner from "../common/FirstPurchaseBanner";
import CartTotals from "../common/CartTotals";
import CartPageCrossSellProducts from "./CartPageCrossSellProducts";
import Breadcrumbs from "../../../../../common/components/Breadcrumbs/Breadcrumbs";
import PageSavedForLaterList from "../../../savedForLater/components/Page/PageSavedForLaterList";
import EmptyState from "../common/EmptyState";
import PageFeaturedProducts from "../../../featured/components/Page/PageFeaturedProducts";
import Icon from "../../../../../common/components/Icon/Icon";
import DeliveryEstimateDisplay from "../../../product/components/common/DeliveryEstimateDisplay";

const CartPage: React.FC = () => {
  const { t, ready: areTranslationsReady } = useTranslation();
  const { data: cart, isLoading, isError } = useGetCartQuery();

  const isComponentLoading = isLoading || !areTranslationsReady;

  // State 1: Loading
  if (isComponentLoading) {
    return <CartPageSkeleton />;
  }

  // State 2: Error
  if (isError) {
    return (
      <div className="cart-page">
        <Breadcrumbs type="cart" />
        <p>{t("cartPage.error", "Error loading cart.")}</p>
      </div>
    );
  }

  // State 3: isFetching (We intentionally ignore this state to avoid ui flicker)

  // State 4: Empty Cart (Check this before success render)
  if (!cart || cart.itemsCount === 0) {
    return (
      <div className="cart-page cart-page--empty">
        <Breadcrumbs type="cart" />
        <EmptyState
          iconName="shopping-cart-filled"
          title={t("cartPage.emptyTitle", "Your Cart is Currently Empty")}
          message={t(
            "cartPage.emptyMessage",
            "Before proceeding to checkout, you must add some products to your shopping cart.",
          )}
          buttonText={t("cartPage.returnToShop", "Return to Shop")}
          buttonUrl={window.commonVariables?.shopUrl || "/"}
        >
          {/* Show Saved For Later even on empty cart state */}
          <PageSavedForLaterList />

          {/* Featured Content for Empty State */}
          <PageFeaturedProducts />
        </EmptyState>
      </div>
    );
  }

  // State 5: Success with items
  return (
    <>
      <div className="cart-page">
        <Breadcrumbs type="cart" />

        <h1 className="cart-page__title">{t("cartPage.title", "Cart")}</h1>

        {/* --- DISPLAY ACTIVE CART VALIDATION ERRORS --- */}
        {cart.errors && cart.errors.length > 0 && (
          <div className="cart-page__alerts" style={{ marginBottom: "2rem" }}>
            {cart.errors.map((err) => (
              <div
                key={err.code}
                className="alert alert--warning"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "12px 16px",
                  backgroundColor: "var(--color-background-soft)",
                  borderLeft: "4px solid var(--color-warning)",
                  borderRadius: "4px",
                  marginBottom: "10px",
                  fontSize: "14px",
                }}
              >
                <Icon
                  iconName="exclamation-circle"
                  style={{
                    color: "var(--color-warning)",
                    fontSize: "18px",
                    flexShrink: 0,
                  }}
                />
                <span>{err.message}</span>
              </div>
            ))}
          </div>
        )}

        <div className="cart-page__actions">
          <a
            href={window.commonVariables?.shopUrl || "/"}
            className="cart-page__continue-shopping-btn"
          >
            <Icon iconName="shopping-bag" className="icon--stroke-thick" />
            <span>{t("cartPage.continueShopping", "Continue Shopping")}</span>
          </a>
        </div>

        {/* Only show Shipping Progress if physical items exist */}
        {cart.needsShipping && <FreeShippingProgressBar />}

        {/* Show First Purchase Banner */}
        <FirstPurchaseBanner />

        <div className="cart-page__content">
          <div className="cart-page__main-content">
            <CartPageList cart={cart} />
            <PageSavedForLaterList />
            <CartPageCrossSellProducts />
          </div>
          <div className="cart-page__sidebar">
            <div className="cart-page__coupon-form">
              <h3 className="cart-page__coupon-form-title">
                {t("cartApplyCouponForm.title", "Have a Coupon?")}
              </h3>
              <CartApplyCouponForm />
            </div>

            {/* Conditionally show shipping calculator */}
            {cart.needsShipping && (
              <CartShippingCalculator
                namePrefix="page"
                isModalContext={false}
              />
            )}

            {/* Conditionally show delivery estimate */}
            {cart.needsShipping && (
              <DeliveryEstimateDisplay estimate={cart.deliveryEstimation} />
            )}

            <CartTotals cart={cart} context="page" showViewCartButton={false} />
          </div>
        </div>
      </div>
    </>
  );
};

export default CartPage;
